import React, { useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';

const mobilePlatformWidth = '420px';
const InputStack = styled('div')<{ isCondensed: boolean }>`
    display: flex;
    gap: ${({ isCondensed }) => (isCondensed ? '5px' : '10px')};
    margin-bottom: 20px;
    justify-content: center;
    width: 100%;
    max-width: ${({ isCondensed }) => (isCondensed ? '170px' : 'none')};
    margin-left: auto;
    margin-right: auto;
`;

const CodeCharacter = styled('div')<{ isCondensed?: boolean }>`
    background-color: #f5f5f5;
    border: none;
    border-radius: 5px;
    padding: ${({ isCondensed }) => (isCondensed ? '2px' : '7px')};

    input {
        width: ${({ isCondensed }) => (isCondensed ? '22px' : '30px')};
        height: ${({ isCondensed }) => (isCondensed ? '36px' : '50px')};
        font-size: ${({ isCondensed }) => (isCondensed ? '20px' : '30px')};
        text-align: center;
        text-transform: uppercase;
        background: transparent;
        border: none;
        outline: none;
        border-bottom: 2px solid #f5f5f5;
        //caret-color: transparent; /* Just in case they want the caret to now show */

        &:focus {
            border-bottom: 2px solid #3598da;
        }
    }
`;

interface Props {
    length?: number;
    onComplete?: (code: string) => void;
}

const CodeInput = ({ length = 6, onComplete }: Props) => {
    const [values, setValues] = useState<string[]>(Array(length).fill(''));
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const isCondensedMedia = useMediaQuery(`(max-width:${mobilePlatformWidth})`, { noSsr: true });

    const handleChange = (value: string, index: number) => {
        const char = value.toUpperCase();

        if (/^[A-Z0-9]?$/.test(char)) {
            const newValues = [...values];
            newValues[index] = char;
            setValues(newValues);

            if (char && index < length - 1) {
                inputsRef.current[index + 1]?.focus();
            }

            if (newValues.every(val => val !== '')) {
                onComplete?.(newValues.join(''));
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const paste = e.clipboardData.getData('text').toUpperCase().replace(/\s+/g, '');
        if (paste.length === length && /^[A-Z0-9]+$/.test(paste)) {
            const newValues = paste.split('');
            setValues(newValues);
            newValues.forEach((char, i) => {
                inputsRef.current[i]!.value = char;
            });
            inputsRef.current[length - 1]?.focus();
            onComplete?.(newValues.join(''));
            e.preventDefault();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !values[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    return (
        <InputStack isCondensed={isCondensedMedia}>
            {values.map((val, i) => (
                <CodeCharacter key={i} isCondensed={isCondensedMedia}>
                    <input
                        ref={el => (inputsRef.current[i] = el)}
                        value={val}
                        onChange={e => handleChange(e.target.value, i)}
                        onKeyDown={e => handleKeyDown(e, i)}
                        onPaste={i === 0 ? handlePaste : undefined}
                        maxLength={1}
                    />
                </CodeCharacter>
            ))}
        </InputStack>
    );
};
export default CodeInput;
